/**
 * ConditionEvaluator - Declarative condition system
 *
 * Evaluates data-dr-show-when, data-dr-hide-when, and data-dr-effect-when
 * attributes against cached section data. Supports OR logic via pipe separator.
 *
 * Condition format: sectionId.field=value
 * OR logic:         sectionId.field=value1|sectionId.field=value2
 */

interface ParsedCondition {
  sectionId: string;
  field: string;
  value: string;
}

export class ConditionEvaluator {
  /**
   * Evaluate all declarative conditions against the current data.
   */
  evaluateAll(cachedData: Record<string, Record<string, any>>): void {
    this.evaluateShowWhen(cachedData);
    this.evaluateHideWhen(cachedData);
    this.evaluateEffects(cachedData);
  }

  /**
   * data-dr-show-when: element visible only when condition matches
   */
  private evaluateShowWhen(cachedData: Record<string, Record<string, any>>): void {
    document.querySelectorAll('[data-dr-show-when]').forEach(el => {
      const attr = el.getAttribute('data-dr-show-when');
      if (!attr) return;
      const conditions = this.parseCondition(attr);
      const match = this.conditionMatches(conditions, cachedData);
      (el as HTMLElement).style.display = match ? '' : 'none';
    });
  }

  /**
   * data-dr-hide-when: element hidden when condition matches
   */
  private evaluateHideWhen(cachedData: Record<string, Record<string, any>>): void {
    document.querySelectorAll('[data-dr-hide-when]').forEach(el => {
      const attr = el.getAttribute('data-dr-hide-when');
      if (!attr) return;
      const conditions = this.parseCondition(attr);
      const match = this.conditionMatches(conditions, cachedData);
      (el as HTMLElement).style.display = match ? 'none' : '';
    });
  }

  /**
   * data-dr-effect-when + data-dr-effect: apply/remove CSS when condition matches
   */
  private evaluateEffects(cachedData: Record<string, Record<string, any>>): void {
    document.querySelectorAll('[data-dr-effect-when]').forEach(el => {
      const attr = el.getAttribute('data-dr-effect-when');
      if (!attr) return;
      const effect = el.getAttribute('data-dr-effect') || '';
      const conditions = this.parseCondition(attr);
      const match = this.conditionMatches(conditions, cachedData);
      const htmlEl = el as HTMLElement;

      effect.split(';').filter(Boolean).forEach(rule => {
        const colonIndex = rule.indexOf(':');
        if (colonIndex === -1) return;
        const prop = rule.substring(0, colonIndex).trim();
        const val = rule.substring(colonIndex + 1).trim();
        if (prop) {
          (htmlEl.style as any)[prop] = match ? val : '';
        }
      });
    });
  }

  /**
   * Parse a condition string into structured conditions.
   * Supports OR via pipe: "hero.heroType=image|hero.heroType=carousel"
   */
  private parseCondition(attr: string): ParsedCondition[] {
    return attr.split('|').map(cond => {
      const trimmed = cond.trim();
      const eqIndex = trimmed.indexOf('=');
      const path = trimmed.substring(0, eqIndex);
      const value = trimmed.substring(eqIndex + 1);
      const dotIndex = path.indexOf('.');
      return {
        sectionId: path.substring(0, dotIndex),
        field: path.substring(dotIndex + 1),
        value,
      };
    });
  }

  /**
   * Check if any condition in the OR set matches the cached data.
   */
  private conditionMatches(
    conditions: ParsedCondition[],
    cachedData: Record<string, Record<string, any>>
  ): boolean {
    return conditions.some(c => {
      const fieldValue = cachedData[c.sectionId]?.[c.field];
      return String(fieldValue ?? '') === c.value;
    });
  }
}
